// Fiche d'un play (D-043, maquette « Coach · fiche d'un play ») : le dessin en grand,
// temps par temps et variante par variante, la lecture animée, les consignes et les
// vidéos ; pour le staff, « vu par les joueuses » (et relancer), « Au match » (plays
// du prochain match), « Dans une séance » (prochain entraînement), modifier.
import {
  addMatchPlay, deletePlay, editorUrl, markPlaySeen, matchPlays, playById, playRemind, playUnseen, removeMatchPlay,
  saveSessionPlan, sessionPlan, teamPlayViews, teamPlays, updatePlay, visibleEvents,
  type Play, type PlaySide,
} from '../lib/api';
import { addDays, agenda, localDate, parseLocalDate } from '../domain/schedule';
import { frameSvg, playFrames } from '../domain/court';
import { squadName, teamOf, type Ctx } from './context';
import { agree, playerNoun, type TeamGender } from '../domain/labels';
import { $, $$, confirmTwice, html, mount, openSheet, toast } from './dom';
import { mediaBlock, SIDE_LABEL } from './playbook';
import { nextMatchOf } from './basket';

const SIDE_ORDER: PlaySide[] = ['attaque', 'defense', 'transition', 'remise', 'special', 'concept'];
const shortDay = (d: string) => parseLocalDate(d).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });

export async function renderPlayPage(root: HTMLElement, ctx: Ctx, playId: string): Promise<void> {
  const p = await playById(playId);
  if (!p) { mount(root, html`<section class="screen"><a class="back" href="#/basket">‹ Plays</a><div class="empty"><p class="empty-title">Play introuvable.</p></div></section>`); return; }
  const team = p.team_id ? teamOf(ctx, p.team_id) : null;
  const staff = !!p.team_id && ctx.staffOf.has(p.team_id);
  if (!staff) void markPlaySeen(p.id);
  const [siblings, next] = await Promise.all([
    p.team_id ? teamPlays(p.team_id).catch(() => [] as Play[]) : Promise.resolve([] as Play[]),
    staff && p.team_id ? nextMatchOf(p.team_id) : Promise.resolve(null),
  ]);
  const order = siblings.sort((a, b) => SIDE_ORDER.indexOf(a.side) - SIDE_ORDER.indexOf(b.side) || a.title.localeCompare(b.title));
  const idx = order.findIndex(x => x.id === p.id);
  const prev = idx > 0 ? order[idx - 1] : null, nxt = idx >= 0 && idx < order.length - 1 ? order[idx + 1] : null;
  const [views, unseen, inMatch] = await Promise.all([
    staff && p.team_id ? teamPlayViews(p.team_id).then(v => v.find(x => x.play_id === p.id) ?? null).catch(() => null) : Promise.resolve(null),
    staff && p.published ? playUnseen(p.id).catch(() => []) : Promise.resolve([]),
    next ? matchPlays(next.event_id).catch(() => []) : Promise.resolve([]),
  ]);
  const inNext = inMatch.some(m => m.play_id === p.id);
  const nextDay = next ? shortDay(localDate(new Date(next.events.starts_at))) : '';
  let anim = 0, step = 0;
  const reload = () => renderPlayPage(root, ctx, playId);
  const back = `#/basket/plays/${p.team_id ?? ''}`;

  mount(root, html`
    <section class="screen pp">
      <div class="pp-nav">
        <a class="back" href="${back}">‹ Plays</a>
        <span class="pp-arrows">
          ${prev ? html`<a class="pp-arrow" href="#/play/${prev.id}" aria-label="Play précédent : ${prev.title}">‹</a>` : ''}
          ${nxt ? html`<a class="pp-arrow" href="#/play/${nxt.id}" aria-label="Play suivant : ${nxt.title}">›</a>` : ''}
        </span>
      </div>
      <header class="screen-head">
        <p class="eyebrow eyebrow-accent">${[SIDE_LABEL[p.side], p.tag, team?.name].filter(Boolean).join(' · ')}</p>
        <h2>${p.title}</h2>
      </header>
      ${p.animations.length ? html`
        <section class="pp-court">
          <div class="pp-svg" id="pp-svg"></div>
          <div class="pp-ctrl">
            <a class="pp-play" href="${editorUrl({ play: p.id, anim: p.animations[0]!.id, view: true })}" id="pp-play" aria-label="Lire l'animation">▶</a>
            <span class="pp-steps" id="pp-steps"></span>
            <button type="button" class="pp-step" id="pp-prev" aria-label="Temps précédent">‹</button>
            <button type="button" class="pp-step" id="pp-next" aria-label="Temps suivant">›</button>
          </div>
        </section>
        ${p.animations.length > 1 ? html`<div class="chip-scroll" role="group" aria-label="Variantes">${p.animations.map((a, i) => html`
          <button type="button" class="chip-toggle chip-sm" data-anim="${i}" aria-pressed="${String(i === 0)}">${a.title || (i === 0 ? 'Principale' : `Variante ${i + 1}`)}</button>`)}</div>` : ''}`
        : html`<section class="block"><p class="muted">${staff ? 'Pas encore de dessin.' : 'Pas de dessin pour ce play.'}</p>
            ${staff ? html`<a class="btn btn-primary" href="${editorUrl({ play: p.id })}">Dessiner le play</a>` : ''}</section>`}
      ${staff ? html`
        <section class="block pp-seen">
          <span class="eyebrow">Vu par l’équipe</span>
          ${p.published ? html`
            <div class="pp-seen-row"><span class="bk-bar"><span style="width:${views && views.roster ? Math.round((views.seen / views.roster) * 100) : 0}%"></span></span>
              <b>${views ? `${views.seen} / ${views.roster}` : '—'}</b></div>
            ${unseen.length ? html`<p class="small">Pas encore : ${unseen.map(u => u.name).join(', ')}</p>
              <button type="button" class="btn btn-ghost btn-sm" id="pp-remind">Relancer</button>` : html`<p class="small">${agree(team?.gender ?? 'X', 'Tous', 'Toutes')} l'ont vu.</p>`}`
          : html`<p class="small">Gardé pour les coachs. <button type="button" class="btn-link-accent" id="pp-publish">Le montrer à l’équipe</button></p>`}
        </section>` : ''}
      ${p.description ? html`<section class="block"><span class="eyebrow">Consignes</span><p class="pb-desc">${p.description}</p></section>` : ''}
      ${p.videos.length || p.images.length ? html`<section class="block">${mediaBlock(p.videos, p.images)}</section>` : ''}
      ${staff ? html`<button type="button" class="btn btn-ghost" id="pp-edit">Modifier le play (nom, consignes, vidéos)</button>` : ''}
      ${staff ? html`
        <div class="pp-bar">
          ${next ? html`<button type="button" class="btn ${inNext ? 'btn-ghost' : 'btn-primary'}" id="pp-match">${inNext ? `Retirer du match` : `Au match du ${nextDay}`}</button>` : ''}
          <button type="button" class="btn btn-ghost" id="pp-seance">Dans une séance</button>
          ${p.animations.length ? html`<a class="btn btn-ghost" href="${editorUrl({ play: p.id, anim: p.animations[0]!.id })}" id="pp-draw">Modifier le dessin</a>` : ''}
        </div>` : ''}
    </section>`);

  // Le dessin : temps par temps, la ligne principale puis ses variantes à la suite (Idries : « si j'ai plusieurs
  // branches, affiche-les à la suite des temps de la ligne principale »), animation par animation.
  const draw = () => {
    const a = p.animations[anim] as never;
    const frames = playFrames(a);
    const n = frames.length;
    const f = frames[Math.min(step, Math.max(0, n - 1))];
    const svg = frameSvg(a, f);
    const box = $(root, '#pp-svg');
    if (!box) return;
    box.innerHTML = svg ?? '<p class="muted">Dessin à compléter dans l’éditeur.</p>';
    $(root, '#pp-steps')!.textContent = !f ? '' : f.branch ? `${f.branch} · temps ${f.index + 1} / ${f.count}` : `Temps ${f.index + 1} / ${f.count}`;
    $<HTMLButtonElement>(root, '#pp-prev')!.disabled = step <= 0;
    $<HTMLButtonElement>(root, '#pp-next')!.disabled = step >= n - 1;
    const play = $<HTMLAnchorElement>(root, '#pp-play');
    if (play) play.href = editorUrl({ play: p.id, anim: p.animations[anim]!.id, view: true });
    const drawLink = $<HTMLAnchorElement>(root, '#pp-draw');
    if (drawLink) drawLink.href = editorUrl({ play: p.id, anim: p.animations[anim]!.id });
  };
  if (p.animations.length) {
    draw();
    $(root, '#pp-prev')!.addEventListener('click', () => { step = Math.max(0, step - 1); draw(); });
    $(root, '#pp-next')!.addEventListener('click', () => { step += 1; draw(); });
    for (const b of $$<HTMLButtonElement>(root, '[data-anim]')) b.addEventListener('click', () => {
      anim = Number(b.dataset.anim); step = 0;
      $$(root, '[data-anim]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      draw();
    });
  }

  $(root, '#pp-remind')?.addEventListener('click', async (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    b.disabled = true;
    try { const n = await playRemind(p.id); toast(n ? `Relance envoyée (${n} compte${n > 1 ? 's' : ''})` : 'Déjà relancées aujourd’hui'); }
    catch { b.disabled = false; toast('Relance impossible.', 'ko'); }
  });
  $(root, '#pp-publish')?.addEventListener('click', async () => {
    try { await updatePlay(p.id, { published: true }); toast('Visible de l’équipe'); await reload(); }
    catch { toast('Impossible pour le moment.', 'ko'); }
  });
  $(root, '#pp-match')?.addEventListener('click', async () => {
    if (!next) return;
    try {
      if (inNext) { await removeMatchPlay(next.event_id, p.id); toast('Retiré des plays du match'); }
      else { await addMatchPlay(next.event_id, p.id, inMatch.length); toast(`Ajouté aux plays du match (${squadName(ctx, next.squad_id) ?? team?.name ?? ''} ${next.is_home === false ? 'à' : '–'} ${next.opponent})`); }
      await reload();
    } catch { toast('Impossible pour le moment.', 'ko'); }
  });
  $(root, '#pp-seance')?.addEventListener('click', async () => {
    if (!p.team_id) return;
    const today = localDate(new Date());
    const events = await visibleEvents().catch(() => []);
    const trainings = agenda(events.filter(e => e.team_id === p.team_id && e.kind === 'training' && !e.cancelled_at), today, addDays(today, 21)).slice(0, 4);
    if (!trainings.length) { toast('Aucun entraînement programmé dans les 3 semaines.', 'ko'); return; }
    openSheet('Dans quelle séance ?', html`<ul class="pick-list">${trainings.map((o, i) => html`<li><button type="button" data-tr="${i}">
      <span class="pick-main"><strong>${shortDay(o.date)}</strong><span class="muted small">${events.find(e => e.id === o.eventId)?.title ?? ''}</span></span>
      <span class="chip chip-ok">Ajouter</span></button></li>`)}</ul>`, (sheet, close) => {
      for (const b of $$<HTMLButtonElement>(sheet, '[data-tr]')) b.addEventListener('click', async () => {
        const o = trainings[Number(b.dataset.tr)]!;
        const plan = (await sessionPlan(o.eventId, o.date).catch(() => null)) ?? { event_id: o.eventId, occurrence_date: o.date, items: [], coach_notes: null, published: false };
        try {
          await saveSessionPlan({ ...plan, items: [...plan.items, {
            id: crypto.randomUUID().slice(0, 8), title: p.title, minutes: 10, category: 'tactique', description: p.description, play_id: p.id,
          }] });
          close(); location.hash = `#/seance/${o.eventId}/${o.date}`;
        } catch { toast('Impossible pour le moment.', 'ko'); }
      });
    });
  });
  $(root, '#pp-edit')?.addEventListener('click', () => openPlayEdit(p, reload, back, team?.gender ?? 'X'));
}

/** Modifier un play : nom, catégorie, repère, consignes, vidéos, visibilité, suppression. */
function openPlayEdit(p: Play, onDone: () => void, back: string, gender: TeamGender = 'X') {
  openSheet('Modifier le play', html`
    <form class="form" id="f-pe" novalidate>
      <label class="field"><span>Nom</span><input name="title" required maxlength="80" value="${p.title}"></label>
      <div class="row-2">
        <label class="field"><span>Catégorie</span><select name="side">${SIDE_ORDER.map(s => html`<option value="${s}" ${s === p.side ? 'selected' : ''}>${SIDE_LABEL[s]}</option>`)}</select></label>
        <label class="field"><span>Repère</span><input name="tag" maxlength="40" value="${p.tag ?? ''}" placeholder="ex. HORNS"></label>
      </div>
      <label class="field"><span>Consignes</span><textarea name="description" rows="4" maxlength="4000">${p.description ?? ''}</textarea></label>
      <div class="field"><span>Vidéos</span>
        ${p.videos.length ? html`<ul class="ce-rows">${p.videos.map((v, i) => html`<li><span>${v.url}</span><button type="button" class="sheet-x" data-vdel="${i}" aria-label="Retirer la vidéo">×</button></li>`)}</ul>` : ''}
        <input name="video" type="url" inputmode="url" placeholder="Ajouter un lien (YouTube, Instagram…)">
      </div>
      <label class="check"><input type="checkbox" name="published" ${p.published ? 'checked' : ''}><span><strong>Visible de l’équipe</strong><br><span class=\"muted\">Les ${playerNoun(gender, 2)} le revoient, animé, sur leur téléphone.</span></span></label>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Enregistrer</button>
      <button type="button" class="btn btn-danger-ghost" id="b-pdel">Supprimer le play</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-pe')!;
    let videos = [...p.videos];
    for (const b of $$<HTMLButtonElement>(sheet, '[data-vdel]')) b.addEventListener('click', () => {
      videos = videos.filter((_, i) => i !== Number(b.dataset.vdel)); b.closest('li')!.remove();
    });
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      const d = new FormData(f);
      const title = String(d.get('title') ?? '').trim();
      const url = String(d.get('video') ?? '').trim();
      if (!title) { $(f, '.form-msg')!.textContent = 'Donne un nom au play.'; return; }
      if (url && !/^https:\/\/\S+$/.test(url)) { $(f, '.form-msg')!.textContent = 'Le lien doit commencer par https://'; return; }
      try {
        await updatePlay(p.id, {
          title, side: String(d.get('side')) as PlaySide, tag: String(d.get('tag') ?? '').trim() || null,
          description: String(d.get('description') ?? '').trim() || null, published: d.get('published') === 'on',
          videos: url ? [...videos, { url, orientation: 'horizontal' }] : videos,
        });
        close(); toast('Play enregistré'); onDone();
      } catch { $(f, '.form-msg')!.textContent = 'Enregistrement impossible.'; }
    });
    confirmTwice($<HTMLButtonElement>(sheet, '#b-pdel')!, `Confirmer : supprimer « ${p.title} »`, async () => {
      try { await deletePlay(p.id); close(); toast('Play supprimé'); location.hash = back; }
      catch { toast('Suppression impossible.', 'ko'); }
    });
  });
}
